import type {
  CompanyDto,
  EvaluationProtocolElementDto,
  ScenarioContextDto,
  ScenarioContextFactDto,
  ScenarioContextLocationDto,
  ScenarioPersonaDto,
  VariableSettingsDto
} from '@/api-gen/api.schemas';
import { Llm, PersonaUsage as PersonaUsageEnum, TtsProvider } from '@/api-gen/api.schemas';
import type { ScenarioSettingsType } from 'app/components/ScenarioEditorPage/ScenarioSettings/hooks/useScenarioForm.ts';

import { useTranslation } from 'react-i18next';
import { I18nNamespace } from '@/i18n/types/i18nNamespace.ts';
import type { I18nScenarioEditorNs, I18nValidationNs } from '@/i18n/dictionaries/interfaces.ts';
import { z, ZodIssueCode } from 'zod';

export function useValidationSchema(): z.ZodType<ScenarioSettingsType> {
  const [translate] = useTranslation([I18nNamespace.ScenarioEditor]);
  const [translateValidation] = useTranslation([I18nNamespace.Validation]);

  const requiredMessage = translate(nameof.full<I18nScenarioEditorNs>((n) => n.validation.thisFieldIsRequired));

  const contextFactSchema = z.object({
    id: z.number().nullable().optional(),
    text: z.string().min(1, { message: requiredMessage }).max(100000)
  }) as z.ZodType<ScenarioContextFactDto>;

  const contextLocationSchema = z.object({
    id: z.number().nullable().optional(),
    location: z.string().min(1, { message: requiredMessage }).max(100)
  }) as z.ZodType<ScenarioContextLocationDto>;

  const schema = z
    .object({
      id: z.number().nullable().optional(),
      name: z.string().min(1, { message: requiredMessage }),
      description: z.string().nullable().optional(),
      langCode: z.string().min(1, { message: requiredMessage }),
      ttsProvider: z.nativeEnum(TtsProvider).nullable().optional(),
      evaluationProtocolElements: z.array(z.custom<EvaluationProtocolElementDto>()).nullable().optional(),
      notesEvaluationProtocolElements: z.array(z.custom<EvaluationProtocolElementDto>()).nullable().optional(),
      scenarioContextIntro: z.string().trim().nullable().optional(),
      playScenarioContextOn: z.boolean(),
      context: z.object({
        id: z.number().nullable().optional(),
        facts: z.array(contextFactSchema),
        locations: z.array(contextLocationSchema)
      }) as z.ZodType<ScenarioContextDto>,
      isLlmEnabled: z.boolean(),
      isDecisionSupportEnabled: z.boolean(),
      notesEnabled: z.boolean(),
      llm: z.nativeEnum(Llm).nullable().optional(),
      useStreaming: z.boolean(),
      maxTokens: z.preprocess((value) => {
        if (value === null || value === undefined || value === '') {
          return null;
        }

        const parsedValue = typeof value === 'string' ? Number(value) : value;
        return Number.isNaN(parsedValue as number) ? value : parsedValue;
      }, z.number().nullable().optional()),
      variables: z.array(z.custom<VariableSettingsDto>()).nullable().optional(),
      createSharedScenario: z.boolean().nullable().optional(),
      scenarioPersonas: z.array(z.custom<ScenarioPersonaDto>()),
      companyId: z.number().nullable().optional(),
      sharedCompanies: z.array(z.custom<CompanyDto>()).nullable().optional(),
      isPublished: z.boolean(),
      speed: z.number().optional(),
      sex: z.any().optional(),
      voiceName: z.string().nullable().optional(),
      voiceId: z.string().nullable().optional()
    })
    .passthrough()
    .superRefine((data, ctx) => {
      const personas = data.scenarioPersonas ?? [];
      if (!personas.length) {
        ctx.addIssue({
          code: ZodIssueCode.custom,
          path: ['scenarioPersonas'],
          message: translateValidation(nameof.full<I18nValidationNs>((n) => n.personaValidation.exactlyOneDefaultRequired))
        });
        return;
      }

      const defaultPersonas = personas.filter((p) => p.personaUsages?.includes(PersonaUsageEnum.Default));
      if (defaultPersonas.length !== 1) {
        ctx.addIssue({
          code: ZodIssueCode.custom,
          path: ['scenarioPersonas'],
          message: translateValidation(nameof.full<I18nValidationNs>((n) => n.personaValidation.exactlyOneDefaultRequired))
        });
      }

      const scenarioLang = data.langCode;
      if (scenarioLang && personas.length) {
        const mismatched = personas.filter(
          (p) => p.persona?.languageCode && p.persona.languageCode !== scenarioLang
        );

        if (mismatched.length > 0) {
          const personaList = mismatched.map((p) => p.persona?.name || `#${p.personaId}`).join(', ');
          ctx.addIssue({
            code: ZodIssueCode.custom,
            path: ['scenarioPersonas'],
            message: translateValidation(
              nameof.full<I18nValidationNs>((n) => n.personaValidation.languageMismatchDetail),
              { scenarioLanguage: scenarioLang, personaList }
            )
          });
        }
      }

      const missingRoles = personas.some((p) => p.roleId == null);
      if (missingRoles) {
        ctx.addIssue({
          code: ZodIssueCode.custom,
          path: ['scenarioPersonas'],
          message: 'Each persona must have a role selected'
        });
      }
    });

  return schema as z.ZodType<ScenarioSettingsType>;
}
